import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Grid, 
  Typography, 
  Box, 
  Button, 
  IconButton, 
  Rating, 
  Divider, 
  TextField, 
  Paper,
  Tabs,
  Tab,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  Chip,
  Alert,
  Snackbar
} from '@mui/material';
import SeoHead from '../components/common/SeoHead';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { useCart } from '../contexts/CartContext';
import ProductReviews from '../components/products/ProductReviews';

// Firebase imports
import { doc, getDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase/firebase';

// 样式化组件
const ProductImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: 'auto',
  borderRadius: theme.shape.borderRadius,
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.02)'
  }
}));

const ThumbnailImage = styled('img')(({ theme }) => ({
  width: 80,
  height: 80,
  objectFit: 'cover',
  borderRadius: theme.shape.borderRadius / 2,
  cursor: 'pointer',
  border: '2px solid transparent',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.05)'
  }
}));

const SelectedThumbnail = styled(ThumbnailImage)(({ theme }) => ({
  border: `2px solid ${theme.palette.primary.main}`,
  boxShadow: '0 0 0 2px rgba(156, 39, 176, 0.2)'
}));

const QuantityControl = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  width: 'fit-content',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3)
}));

const QuantityButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(1)
}));

const AddToCartButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  padding: theme.spacing(1.5, 4),
  borderRadius: theme.shape.borderRadius,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark
  }
}));

const WishlistButton = styled(IconButton)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1.5),
  marginLeft: theme.spacing(2)
}));

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-tabpanel-${index}`}
      aria-labelledby={`product-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

// 注意: 我们现在从firebase.js导入db和auth

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  
  const [product, setProduct] = useState(null);
  const [productSchemaData, setProductSchemaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [user, setUser] = useState(null);

  // Check authentication state
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      setUser(user);
    });
    return () => unsubscribe();
  }, []);
  
  // Fetch product data from Firestore
  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const productRef = doc(db, 'products', id);
        const productSnap = await getDoc(productRef);
        
        if (!productSnap.exists()) {
          setError('产品不存在');
          setLoading(false);
          return;
        }
        
        const productData = { id: productSnap.id, ...productSnap.data() };
        
        // 创建产品结构化数据
        const schemaData = {
          "@context": "https://schema.org",
          "@type": "Product",
          "name": productData.name || "",
          "image": productData.images && productData.images.length > 0 ? productData.images[0] : "",
          "description": productData.description || "",
          "brand": {
            "@type": "Brand",
            "name": "DestinyCrystal"
          },
          "offers": {
            "@type": "Offer",
            "priceCurrency": "SGD",
            "price": productData.price ? productData.price.toString() : "0",
            "availability": productData.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            "seller": {
              "@type": "Organization",
              "name": "DestinyCrystal命理水晶"
            }
          }
        };
        
        // 如果有评分和评论，添加聚合评分
        if (productData.rating && productData.reviewCount) {
          schemaData.aggregateRating = {
            "@type": "AggregateRating",
            "ratingValue": productData.rating.toString(),
            "reviewCount": productData.reviewCount.toString()
          };
        }
        
        setProductSchemaData(schemaData);
        
        // Fetch category name if categoryId exists
        if (productData.categoryId) {
          try {
            const categoryRef = doc(db, 'categories', productData.categoryId);
            const categorySnap = await getDoc(categoryRef);
            if (categorySnap.exists()) {
              productData.categoryName = categorySnap.data().name;
            }
          } catch (categoryErr) {
            console.error('获取分类信息失败:', categoryErr);
          }
        }
        
        // Make sure images field exists
        if (!productData.images || !Array.isArray(productData.images)) {
          productData.images = [
            'https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/product-default.jpg'
          ];
        }

        // 确保产品有reviews字段，设置默认值
        if (!productData.reviews || !Array.isArray(productData.reviews) || productData.reviews.length === 0) {
          // 如果评价为空，添加一些测试评价数据
          productData.reviews = [
            {
              id: 'review1',
              userId: 'user1',
              userName: '王晓莉',
              rating: 5,
              content: '收到商品后非常惊喜，质量很好。使用后感觉效果明显，非常满意。',
              date: new Date('2024-03-01').toISOString(),
              avatar: 'https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/avatar-default.jpg'
            },
            {
              id: 'review2',
              userId: 'user2',
              userName: '李明',
              rating: 5,
              content: '这个产品的能量很强，收到第一天就能感受到效果。质量也很好，很值这个价格。',
              date: new Date('2024-02-15').toISOString(),
              avatar: 'https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/avatar-default.jpg'
            }
          ];
        }
        
        // 确保有评分和评价数量
        if (!productData.rating) {
          productData.rating = 5.0;
        }
        
        if (!productData.reviewCount) {
          productData.reviewCount = productData.reviews.length;
        }
        
        // 输出到控制台检查评价数据
        console.log('产品数据:', productData);
        console.log('评价数据:', productData.reviews);
        
        setProduct(productData);
        
        // Fetch related products from the same category
        try {
          if (productData.categoryId) {
            const relatedQuery = query(
              collection(db, 'products'),
              where('categoryId', '==', productData.categoryId),
              where('status', '==', 'active')
            );
            const relatedSnap = await getDocs(relatedQuery);
            const relatedData = relatedSnap.docs
              .map(doc => ({ id: doc.id, ...doc.data() }))
              .filter(item => item.id !== id)
              .slice(0, 3);
            
            setRelatedProducts(relatedData);
          }
        } catch (relatedErr) {
          console.error('获取相关产品失败:', relatedErr);
          // Continue even if related products fail
        }
        
        setLoading(false);
      } catch (err) {
        console.error('获取产品信息失败:', err);
        setError('获取产品信息失败，请稍后重试');
        setLoading(false);
      }
    };
    
    if (id) {
      fetchProduct();
    }
  }, [id]);
  
  // Check if product is in user's favorites
  useEffect(() => {
    const checkFavorite = async () => {
      if (!user || !product) return;
      
      try {
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const favorites = userData.favoriteProducts || [];
          setIsFavorite(favorites.includes(product.id));
        }
      } catch (err) {
        console.error('检查收藏状态失败:', err);
      }
    };
    
    checkFavorite();
  }, [user, product]);
  
  const handleQuantityChange = (increment) => {
    const newQuantity = quantity + increment;
    if (newQuantity >= 1 && newQuantity <= (product?.stock || 1)) {
      setQuantity(newQuantity);
    }
  };
  
  const handleAddToCart = async () => {
    if (!product) return;
    
    // 添加到本地购物车
    addItem(product, quantity);
    
    // 触发Google Tag Manager的添加到购物车事件
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'addToCart',
        'ecommerce': {
          'currencyCode': 'SGD',
          'add': {
            'products': [{
              'name': product.name,
              'id': product.id,
              'price': product.salePrice || product.price,
              'category': product.categoryName || '',
              'quantity': quantity
            }]
          }
        }
      });
      console.log('GTM事件：添加到购物车', product.name, '数量:', quantity);
    }
    
    if (user) {
      try {
        // Add to cart in Firestore via the API
        const idToken = await user.getIdToken();
        const response = await fetch('/api/cart', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({
            productId: product.id,
            quantity: quantity
          })
        });
        
        if (!response.ok) {
          throw new Error('加入购物车失败');
        }
        
        setSnackbar({
          open: true,
          message: '已成功加入购物车',
          severity: 'success'
        });
        
        // 不自动跳转，让用户自行决定是否去购物车页面
      } catch (err) {
        console.error('加入购物车出错:', err);
        
        setSnackbar({
          open: true,
          message: '已加入本地购物车，但同步到服务器失败',
          severity: 'warning'
        });
      }
    } else {
      // 用户未登录
      setSnackbar({
        open: true,
        message: '已加入购物车，请登录后查看购物车',
        severity: 'info'
      });
    }
  };
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const toggleFavorite = async () => {
    if (!user) {
      setSnackbar({
        open: true,
        message: '请先登录后再收藏商品',
        severity: 'info'
      });
      return;
    }
    
    try {
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const favorites = userData.favoriteProducts || [];
        
        let updatedFavorites;
        if (favorites.includes(product.id)) {
          updatedFavorites = favorites.filter(id => id !== product.id);
        } else {
          updatedFavorites = [...favorites, product.id];
        }
        
        await updateDoc(userRef, { favoriteProducts: updatedFavorites });
        setIsFavorite(!isFavorite);
        
        setSnackbar({
          open: true,
          message: isFavorite ? '已取消收藏' : '已添加到收藏',
          severity: 'success'
        });
      }
    } catch (err) {
      console.error('更新收藏失败:', err);
      setSnackbar({
        open: true,
        message: '收藏操作失败，请稍后重试',
        severity: 'error'
      });
    }
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography>正在加载产品信息...</Typography>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography color="error">{error}</Typography>
        <Button 
          variant="outlined" 
          color="primary" 
          onClick={() => navigate('/shop')}
          sx={{ mt: 2 }}
        >
          返回商城
        </Button>
      </Container>
    );
  }
  
  if (!product) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography>未找到产品信息</Typography>
        <Button 
          variant="outlined" 
          color="primary" 
          onClick={() => navigate('/shop')}
          sx={{ mt: 2 }}
        >
          返回商城
        </Button>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* 产品SEO优化 */}
      {product && (
        <SeoHead
          title={`${product.name || '产品详情'} | 水晶能量手串 | DestinyCrystal`}
          description={product.description || `精选高品质${product.name}，助您平衡能量、增强运势，每款水晶都经过专业净化与加持。`}
          canonicalUrl={`https://destiny-crystal.com/product/${id}`}
          imageUrl={product.images && product.images.length > 0 ? product.images[0] : ''}
          keywords={[product.name, "水晶手串", "能量水晶", "平衡能量", "提升运势"].concat(product.tags || [])}
          schemaData={productSchemaData}
        />
      )}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {/* 面包屑导航 */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link color="inherit" href="/">首页</Link>
        <Link color="inherit" href="/shop">商城</Link>
        {product.categoryName && (
          <Link color="inherit" href={`/category/${product.categoryId}`}>
            {product.categoryName}
          </Link>
        )}
        <Typography color="text.primary">{product.name}</Typography>
      </Breadcrumbs>
      
      <Grid container spacing={4}>
        {/* 产品图片区 */}
        <Grid item xs={12} md={6}>
          <ProductImage 
            src={
              typeof product.images[selectedImage] === 'string' 
                ? product.images[selectedImage] 
                : product.images[selectedImage]?.url || 'https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/product-default.jpg'
            } 
            alt={product.name} 
          />
          
          <Box 
            sx={{ 
              display: 'flex', 
              gap: 1, 
              mt: 2, 
              flexWrap: 'wrap', 
              justifyContent: 'center' 
            }}
          >
            {product.images.map((image, index) => {
              const imageSrc = typeof image === 'string' ? image : image?.url;
              return (
                index === selectedImage ? (
                  <SelectedThumbnail
                    key={index}
                    src={imageSrc || 'https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/thumbnail-default.jpg'}
                    alt={`Thumbnail ${index + 1}`}
                    onClick={() => setSelectedImage(index)}
                  />
                ) : (
                  <ThumbnailImage
                    key={index}
                    src={imageSrc || 'https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/thumbnail-default.jpg'}
                    alt={`Thumbnail ${index + 1}`}
                    onClick={() => setSelectedImage(index)}
                  />
                )
              );
            })}
          </Box>
        </Grid>
        
        {/* 产品信息区 */}
        <Grid item xs={12} md={6}>
          <Typography variant="h4" gutterBottom>
            {product.name}
          </Typography>
          
          {/* 显示商品库存状态 */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {product.stock > 0 ? (
              <Chip 
                label={`库存: ${product.stock}`} 
                color="success" 
                size="small" 
                variant="outlined" 
                sx={{ mr: 2 }}
              />
            ) : (
              <Chip 
                label="缺货" 
                color="error" 
                size="small" 
                variant="outlined" 
                sx={{ mr: 2 }}
              />
            )}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Rating 
                value={5.0} 
                precision={0.1} 
                readOnly 
                size="small" 
              />
              <Typography variant="body2" sx={{ ml: 1 }}>
                5.0 ({product.reviewCount || 0}条评价)
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}>
            <Typography 
              variant="h5" 
              color="primary" 
              fontWeight="bold"
              sx={{ mr: 2 }}
            >
              S${product.salePrice || product.price}
            </Typography>
            {product.salePrice && product.salePrice < product.price && (
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{ textDecoration: 'line-through' }}
              >
                S${product.price}
              </Typography>
            )}
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="body1" paragraph>
            {product.description}
          </Typography>
          
          {/* 移除了规格显示部分 */}
          
          <Box sx={{ mt: 4 }}>
            <Typography variant="subtitle1" gutterBottom>
              数量
            </Typography>
            <QuantityControl>
              <QuantityButton
                onClick={() => handleQuantityChange(-1)}
                disabled={quantity <= 1}
              >
                <RemoveIcon />
              </QuantityButton>
              <Typography sx={{ mx: 2, minWidth: '40px', textAlign: 'center' }}>
                {quantity}
              </Typography>
              <QuantityButton
                onClick={() => handleQuantityChange(1)}
                disabled={quantity >= (product.stock || 1) || product.stock <= 0}
              >
                <AddIcon />
              </QuantityButton>
            </QuantityControl>
          </Box>
          
          <Box sx={{ display: 'flex', mt: 2 }}>
            <AddToCartButton
              variant="contained"
              onClick={handleAddToCart}
              startIcon={<AddIcon />}
              disabled={product.stock <= 0}
            >
              {product.stock > 0 ? '加入购物车' : '缺货'}
            </AddToCartButton>
            <WishlistButton
              onClick={toggleFavorite}
              aria-label="Add to wishlist"
            >
              {isFavorite ? (
                <FavoriteIcon color="error" />
              ) : (
                <FavoriteBorderIcon />
              )}
            </WishlistButton>
          </Box>
          
          {user && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => navigate('/cart')}
              sx={{ mt: 2, mr: 1 }}
            >
              查看购物车
            </Button>
          )}
          
          <Box 
            sx={{ 
              mt: 3, 
              p: 2, 
              backgroundColor: 'rgba(0,0,0,0.02)', 
              borderRadius: 1 
            }}
          >
            <Typography variant="body2">
              免费配送 — 订单满S$100免运费
            </Typography>
            <Typography variant="body2">
              7天无理由退货 — 品质保证
            </Typography>
          </Box>
        </Grid>
      </Grid>
      
      {/* 产品详情选项卡 */}
      <Box sx={{ mt: 6, mb: 4 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="用户评价" id="product-tab-0" />
          <Tab label="配送与退货" id="product-tab-1" />
        </Tabs>
        
        <TabPanel value={tabValue} index={0}>
          {/* 引入新的评价组件 */}
          <Box sx={{ mt: -2 }}>
            <ProductReviews productId={id} />
          </Box>
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            配送信息
          </Typography>
          <Typography variant="body1" paragraph>
            我们提供全国范围内的快递配送服务。订单处理通常需要1-2个工作日，配送时间根据地区不同，一般为3-7个工作日。
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            退货政策
          </Typography>
          <Typography variant="body1" paragraph>
            自收到商品之日起7天内，如产品未使用且保持原包装完好，可申请无理由退货。部分特殊商品可能不支持无理由退货，详情请查看商品说明。
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            保修政策
          </Typography>
          <Typography variant="body1">
            我们的产品享有30天的免费保修服务，保修范围不包括人为损坏和不当使用造成的损坏。
          </Typography>
        </TabPanel>
      </Box>
      
      {/* 相关产品 */}
      {relatedProducts.length > 0 && (
        <Box sx={{ mt: 8 }}>
          <Typography variant="h5" gutterBottom>
            相关产品
          </Typography>
          <Grid container spacing={3}>
            {relatedProducts.map(relatedProduct => (
              <Grid item xs={12} sm={6} md={4} key={relatedProduct.id}>
                <Card
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <Box
                    sx={{ 
                      position: 'relative',
                      paddingTop: '100%',
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      component="img"
                      src={
                        typeof relatedProduct.images?.[0] === 'string' 
                          ? relatedProduct.images[0] 
                          : relatedProduct.images?.[0]?.url || 'https://via.placeholder.com/300x300?text=Product'
                      }
                      alt={relatedProduct.name}
                      sx={{ 
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.5s ease',
                        '&:hover': {
                          transform: 'scale(1.05)'
                        }
                      }}
                    />
                  </Box>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {relatedProduct.name}
                    </Typography>
                    <Typography 
                      variant="subtitle1" 
                      color="primary" 
                      fontWeight="bold"
                    >
                      S${relatedProduct.salePrice || relatedProduct.price}
                    </Typography>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      sx={{ mt: 2 }}
                      onClick={() => {
                        // Navigate and refresh
                        navigate(`/product/${relatedProduct.id}`);
                        window.scrollTo(0, 0);
                      }}
                    >
                      查看详情
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Container>
  );
};

export default ProductDetail;